import { useState } from 'react'

import { readComicEpisodeDragId, setComicEpisodeDragData } from '../../lib/comicEpisodeDrag'
import { ensureEpisodePages } from '../../lib/comicPages'
import type { ComicEpisode, ComicEpisodeEditPatch } from '../../lib/comicTypes'
import { HorizontalChipStrip } from '../HorizontalChipStrip'
import { ComicEpisodeDirectoryModal } from './ComicEpisodeDirectoryModal'
import { ComicEpisodeEditModal } from './ComicEpisodeEditModal'
import { ComicRailChip } from './ComicRailChip'
import {
  ComicRailAddButton,
  ComicRailDeleteButton,
  ComicRailDirectoryButton,
  ComicRailDivider,
  ComicRailEditButton,
  ComicRailSection,
  ComicRailSectionHeader,
} from './ComicRailSection'
import type { ComicSaveStatus } from './ComicSaveStatusPill'

type Props = {
  angular: boolean
  episodes: ComicEpisode[]
  selectedEpisodeId: string | null
  onSelectEpisode: (episodeId: string) => void
  onAddEpisode?: () => void
  onDeleteEpisode?: (episodeId: string) => void
  onEditEpisode?: (episodeId: string, patch: ComicEpisodeEditPatch) => void
  onReorderEpisode?: (sourceEpisodeId: string, targetEpisodeId: string) => void
  canvasEpisodeIds?: string[]
  episodeSaveStatus?: (episodeId: string) => ComicSaveStatus
  onToggleCanvasEpisode?: (episodeId: string, checked: boolean) => void
  onExportCanvasEpisodes?: () => void
  exportingCanvasZip?: boolean
  wideScreen?: boolean
}

export function ComicEpisodeStrip({
  angular,
  episodes,
  selectedEpisodeId,
  onSelectEpisode,
  onAddEpisode,
  onDeleteEpisode,
  onEditEpisode,
  onReorderEpisode,
  canvasEpisodeIds = [],
  episodeSaveStatus,
  onToggleCanvasEpisode,
  onExportCanvasEpisodes,
  exportingCanvasZip = false,
  wideScreen = false,
}: Props) {
  const [modalOpen, setModalOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editTargetId, setEditTargetId] = useState<string | null>(null)
  const selected =
    episodes.find((e) => e.id === selectedEpisodeId) ?? episodes[0] ?? null
  const editTarget = episodes.find((e) => e.id === editTargetId) ?? null

  const headerActions = (
    <>
      {onAddEpisode ? (
        <ComicRailAddButton
          angular={angular}
          label="新建话"
          tooltip="新建话"
          onClick={onAddEpisode}
        />
      ) : null}
      {onEditEpisode && selected ? (
        <ComicRailEditButton
          angular={angular}
          label={`编辑${selected.title}`}
          tooltip="编辑本话"
          onClick={() => {
            setEditTargetId(selected.id)
            setEditOpen(true)
          }}
        />
      ) : null}
      {onDeleteEpisode && episodes.length > 1 && selected ? (
        <ComicRailDeleteButton
          angular={angular}
          label={`删除${selected.title}`}
          tooltip="删除本话"
          onClick={() => onDeleteEpisode(selected.id)}
        />
      ) : null}
      {!wideScreen ? (
        <ComicRailDirectoryButton
          angular={angular}
          label="调整话次"
          tooltip="调整话次"
          onClick={() => setModalOpen(true)}
        />
      ) : null}
    </>
  )

  return (
    <>
      <ComicRailSection>
        <ComicRailSectionHeader title="话次" actions={headerActions} />
        <HorizontalChipStrip
          className="min-h-[2.25rem] pb-0.5"
          trackClassName="gap-2"
          role="tablist"
          aria-label="漫画话次"
        >
          {episodes.map((ep) => {
            const active = ep.id === selected?.id
            const pageCount = ensureEpisodePages(ep).pages.length
            const draggable = Boolean(onReorderEpisode && episodes.length > 1)
            return (
              <div
                key={ep.id}
                draggable={draggable}
                onDragStart={(event) => {
                  if (!draggable) return
                  setComicEpisodeDragData(event.dataTransfer, ep.id)
                }}
                onDragOver={(event) => {
                  if (!draggable) return
                  const sourceId = readComicEpisodeDragId(event.dataTransfer)
                  if (!sourceId || sourceId === ep.id) return
                  event.preventDefault()
                  event.dataTransfer.dropEffect = 'move'
                }}
                onDrop={(event) => {
                  if (!draggable) return
                  const sourceId = readComicEpisodeDragId(event.dataTransfer)
                  if (!sourceId || sourceId === ep.id) return
                  event.preventDefault()
                  onReorderEpisode?.(sourceId, ep.id)
                }}
                className={draggable ? 'cursor-grab active:cursor-grabbing' : undefined}
              >
                <ComicRailChip
                  angular={angular}
                  active={active}
                  role="tab"
                  aria-selected={active}
                  title={ep.title}
                  meta={`${pageCount} 页`}
                  onClick={() => onSelectEpisode(ep.id)}
                />
              </div>
            )
          })}
        </HorizontalChipStrip>
      </ComicRailSection>
      <ComicRailDivider />

      {!wideScreen ? (
        <ComicEpisodeDirectoryModal
          open={modalOpen}
          angular={angular}
          episodes={episodes}
          activeEpisodeId={selectedEpisodeId}
          canvasEpisodeIds={canvasEpisodeIds}
          episodeSaveStatus={episodeSaveStatus ?? (() => 'saved')}
          onClose={() => setModalOpen(false)}
          onToggleCanvasEpisode={onToggleCanvasEpisode ?? (() => {})}
          onExportCanvasEpisodes={onExportCanvasEpisodes}
          exportingCanvasZip={exportingCanvasZip}
          onEditEpisode={onEditEpisode}
        />
      ) : null}

      {onEditEpisode ? (
        <ComicEpisodeEditModal
          open={editOpen}
          angular={angular}
          episode={editTarget}
          onClose={() => setEditOpen(false)}
          onSave={(patch) => {
            if (editTargetId) onEditEpisode(editTargetId, patch)
          }}
        />
      ) : null}
    </>
  )
}
